import React, { useEffect, useState } from "react";
import api, { formatTL, formatDate } from "../lib/api";
import ProductImage from "../components/ProductImage";
import {
  Package,
  Layers,
  Wallet,
  TrendingUp,
  AlertTriangle,
  Receipt,
  Banknote,
  Percent,
  ShoppingCart,
} from "lucide-react";
import { Link } from "react-router-dom";

const KPI = ({ title, value, icon: Icon, testid, tone = "gold", sub }) => {
  const tones = {
    gold: "text-amber-700 bg-amber-50 border-amber-200",
    red: "text-red-700 bg-red-50 border-red-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    stone: "text-stone-700 bg-stone-100 border-stone-200",
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
  };
  return (
    <div className="card-lux p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-stone-500">
            {title}
          </div>
          <div className="mt-3 font-serif-display text-3xl text-stone-900">
            {value}
          </div>
          {sub && (
            <div className="mt-1 text-xs text-stone-500 font-mono-code">{sub}</div>
          )}
        </div>
        <div className={`p-2.5 rounded-lg border ${tones[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div
        className="mt-4 h-[2px] rounded"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(197,160,89,0.6), transparent)",
        }}
      />
    </div>
  );
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [today, setToday] = useState(null);

  useEffect(() => {
    api.get("/products/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/sales/today-summary").then((r) => setToday(r.data)).catch(() => {});
  }, []);

  if (!summary) {
    return <div className="text-stone-500">Yükleniyor...</div>;
  }

  return (
    <div data-testid="dashboard-root">
      <div className="mb-6">
        <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
          GENEL BAKIŞ
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900 mt-1">
          Mağazanızın nabzı
        </h1>
        <p className="text-stone-500 text-sm mt-1">
          Rivaco stok durumu, günün satışları ve envanter değeri.
        </p>
      </div>

      {/* Today's sales */}
      {today && (
        <>
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="font-serif-display text-xl text-stone-900">Bugün</h2>
            <Link
              to="/satislar"
              className="text-xs text-amber-700 hover:text-amber-900"
            >
              Tüm satışlar →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <KPI title="Bugünkü Ciro" value={formatTL(today.revenue)} icon={TrendingUp} testid="kpi-today-revenue" tone="emerald" />
            <KPI title="Satış Sayısı" value={today.sales_count} icon={Receipt} testid="kpi-today-count" tone="gold" />
            <KPI title="Satılan Ürün" value={today.units_sold} icon={ShoppingCart} testid="kpi-today-units" tone="stone" sub="adet" />
            <KPI title="Nakit" value={formatTL(today.cash)} icon={Banknote} testid="kpi-today-cash" tone="emerald" />
            <KPI title="Havale" value={formatTL(today.transfer)} icon={Wallet} testid="kpi-today-transfer" tone="indigo" />
            <KPI title="İndirim" value={formatTL(today.discount)} icon={Percent} testid="kpi-today-discount" tone="red" />
          </div>
        </>
      )}

      <div className="mb-3">
        <h2 className="font-serif-display text-xl text-stone-900">Envanter</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <KPI
          title="Ürün Çeşidi"
          value={summary.total_variety}
          icon={Layers}
          testid="kpi-total-products"
          sub="Toplam kayıtlı"
        />
        <KPI
          title="Toplam Stok"
          value={summary.total_units}
          icon={Package}
          testid="kpi-total-stock"
          sub="adet"
        />
        <KPI
          title="Maliyet Değeri"
          value={formatTL(summary.total_cost)}
          icon={Wallet}
          testid="kpi-total-cost"
          tone="stone"
        />
        <KPI
          title="Satış Değeri"
          value={formatTL(summary.total_sales_value)}
          icon={TrendingUp}
          testid="kpi-total-sales-value"
          tone="emerald"
        />
        <KPI
          title="Düşük Stok"
          value={summary.low_stock_count}
          icon={AlertTriangle}
          testid="kpi-low-stock-count"
          tone="red"
          sub="ürün uyarıda"
        />
      </div>

      <div className="card-lux p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
              SON EKLENENLER
            </div>
            <h2 className="font-serif-display text-2xl text-stone-900">
              Son Eklenen Ürünler
            </h2>
          </div>
          <Link
            to="/urunler"
            data-testid="dashboard-view-all-products"
            className="text-sm text-amber-700 hover:text-amber-900 font-medium"
          >
            Tümünü gör →
          </Link>
        </div>

        {summary.recent.length === 0 ? (
          <div className="text-center py-12 text-stone-500">
            Henüz ürün eklenmemiş.{" "}
            <Link to="/yeni-urun" className="text-amber-700 underline">
              İlk ürünü ekle
            </Link>
            .
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {summary.recent.map((p) => (
              <div
                key={p.id}
                data-testid={`recent-product-${p.sku}`}
                className="flex items-center gap-4 p-3 rounded-xl border border-stone-200 hover:border-amber-300 hover:bg-amber-50/40 transition"
              >
                <ProductImage path={p.image_path} size={56} alt={p.name} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-stone-900 truncate">
                    {p.name}
                  </div>
                  <div className="text-xs text-stone-500 font-mono-code truncate">
                    {p.sku}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="text-stone-500">{p.stock} adet</span>
                    <span className="text-amber-700 font-semibold">
                      {formatTL(p.sale_price)}
                    </span>
                  </div>
                </div>
                <div className="text-[10px] text-stone-400 font-mono-code">
                  {formatDate(p.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
